/**
 * ResultBlock, the closed-case result (2026-08-27 redesign, v2).
 *
 * Two pieces, in order:
 *  1. The verdict hero, kept from the original VerdictView because it
 *     was the best moment in the portal: "Verdict's in. You saved:"
 *     over the big number on the warm gradient, honest neutral variant
 *     for no-reduction closes. Quiet endings (customer_withdrew,
 *     unable_to_proceed) and unconfirmed historical cases get a plain
 *     card instead, no celebration and no financial claims.
 *  2. "What happens next", three numbered lines: pay (with Mark as
 *     paid), the thread stays open, start the next case. This is the
 *     answer to "what are my options now?" in one place.
 *
 * Renders above the preserved conversation. VerdictView.tsx stays in
 * the tree unrouted as a rollback path.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Gift, Share2 } from 'lucide-react';
import { loadReferralCode, shareReferral } from '../lib/referralShare';
import { supabase } from '../lib/supabase';
import { phVerdictViewed, phBillMarkedPaid, phStartNewCaseClicked, phFailure } from '../lib/posthog';
import type { Bill, CaseOutcome } from '../types/bill';
import {
  CASE_OUTCOME_LABELS,
  CASE_OUTCOME_EXPLANATIONS,
  VERDICT_OUTCOMES,
} from '../lib/case-state';

type Settlement = {
  id: string;
  description: string;
  rationale: string | null;
  was_amount: number;
  now_amount: number;
  saved_amount: number;
  sort_order: number;
};

type Props = {
  bill: Bill;
  outcome: CaseOutcome | null;
  v2: boolean;
};

/* Money with cents, the way a settlement should read. */
const fmt2 = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* Right after a win is when people refer (2026-10-27). One line and a
   share button under the saved amount. Shows nothing without a code. */
function ReferralAsk() {
  const [code, setCode] = useState<string | null>(null);
  const [done, setDone] = useState<'copied' | 'shared' | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadReferralCode().then((c) => {
      if (!cancelled && c) setCode(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  if (!code) return null;
  return (
    <div className="rb-refer">
      <span className="rb-refer-icon" aria-hidden><Gift size={17} /></span>
      <div className="rb-refer-text">
        <p className="rb-refer-title">Know someone with a bill like this?</p>
        <p className="rb-refer-sub">
          They get $40 off. You get a $40 gift card once they&apos;ve started their case.
        </p>
      </div>
      <button
        type="button"
        className="rb-refer-btn"
        onClick={async () => {
          const r = await shareReferral(code, 'case_won');
          if (r !== 'failed') {
            setDone(r);
            setTimeout(() => setDone(null), 2500);
          }
        }}
      >
        {done === 'copied' ? <><Check size={15} /> Link copied</> : <><Share2 size={15} /> Share your link</>}
      </button>
    </div>
  );
}

export default function ResultBlock({ bill, outcome, v2 }: Props) {
  const navigate = useNavigate();
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [paidAt, setPaidAt] = useState<string | null>(
    (bill as { paid_at?: string | null }).paid_at ?? null,
  );
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('bill_settlements')
        .select(
          'id, description, rationale, was_amount, now_amount, saved_amount, sort_order',
        )
        .eq('bill_id', bill.id)
        .order('sort_order', { ascending: true });
      if (!cancelled) {
        setSettlements(data ?? []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bill.id]);

  /* Structured saved amount when settlements exist, legacy
     bills.amount_saved otherwise, same fallback VerdictView used. */
  const savedTotal =
    settlements && settlements.length > 0
      ? settlements.reduce((acc, s) => acc + Number(s.saved_amount), 0)
      : Number(bill.amount_saved) || 0;
  const wasTotal =
    settlements && settlements.length > 0
      ? settlements.reduce((acc, s) => acc + Number(s.was_amount), 0)
      : Number(bill.bill_amount) || 0;
  const nowTotal = Math.max(wasTotal - savedTotal, 0);
  const percentSaved = wasTotal > 0 ? Math.round((savedTotal / wasTotal) * 100) : 0;

  /* ── Classification ── */
  const historical = v2 && outcome === null;
  const quiet =
    outcome === 'customer_withdrew' ||
    outcome === 'unable_to_proceed' ||
    outcome === 'customer_unresponsive';
  const verdictCapable = v2
    ? outcome !== null && VERDICT_OUTCOMES.includes(outcome)
    : true;
  const noSavings =
    outcome === 'no_reduction' ||
    (!v2 && (savedTotal <= 0 || bill.status === 'no_savings'));
  const win = verdictCapable && !noSavings && savedTotal > 0;

  /* Prose only ever names the PROVIDER. display_name defaults to the
     uploaded filename, which produced sentences like "Pay scan.png
     through their portal". */
  const providerName = bill.provider_name || 'the provider';

  const outcomeLabel = outcome ? CASE_OUTCOME_LABELS[outcome] : null;

  const explanation = historical
    ? "This case closed before our new result tracking. We're confirming exactly how it ended and will post the details here as soon as that's done."
    : outcome
    ? CASE_OUTCOME_EXPLANATIONS[outcome]
    : win
    ? null
    : 'Agent Loop audited every line on this bill and the charges held up under scrutiny. No errors, no overcharges, no negotiation leverage.';

  /* The shared outcome explanation promises "the figures below"; when
     this ending renders no figures, keep just the first sentence. */
  const explainNoFigures =
    explanation?.replace(' The figures below are what changed.', '') ?? null;

  /* ── The savings reveal, kept tracked (2026-08-08). Fires once per
     bill after settlements resolve, same payload the VerdictView
     emitted so the retention funnel keeps its history. Quiet endings
     and unconfirmed historical cases never made claims, so they never
     fired, and still don't. ── */
  const verdictTracked = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (historical || quiet || !verdictCapable) return;
    if (verdictTracked.current === bill.id) return;
    verdictTracked.current = bill.id;
    phVerdictViewed({
      billId: bill.id,
      wasTotal,
      savedTotal,
      percentSaved,
      noSavings,
      status: bill.status,
    });
  }, [loading, historical, quiet, verdictCapable, bill.id, bill.status, wasTotal, savedTotal, percentSaved, noSavings]);

  /* Self-report toggle through the mark_bill_paid RPC (migration 048),
     RLS blocks direct customer UPDATEs on bills. */
  const handleMarkAsPaid = async () => {
    if (markingPaid) return;
    const wantPaid = !paidAt;
    const previousPaidAt = paidAt;
    setMarkingPaid(true);
    setPaidAt(wantPaid ? new Date().toISOString() : null);
    const { data, error } = await supabase.rpc('mark_bill_paid', {
      bill_id: bill.id,
      paid: wantPaid,
    });
    if (error) {
      console.error('Failed to toggle paid_at via mark_bill_paid', error);
      phFailure({
        step: 'mark_bill_paid',
        message: error.message,
        context: { bill_id: bill.id },
      });
      setPaidAt(previousPaidAt);
    } else {
      setPaidAt((data as string | null) ?? null);
      phBillMarkedPaid({ billId: bill.id, paid: wantPaid, savedTotal });
    }
    setMarkingPaid(false);
  };

  const [saveDollars, saveCents] = fmt2(savedTotal).split('.');

  const paidButton = (
    <button
      type="button"
      className={`rb-paid ${paidAt ? 'is-paid' : ''}`}
      onClick={handleMarkAsPaid}
      disabled={markingPaid}
    >
      {paidAt ? (
        <>
          <Check size={13} aria-hidden /> Marked paid
        </>
      ) : markingPaid ? 'Saving…' : 'Mark as paid'}
    </button>
  );

  const newCaseLink = (
    <button
      type="button"
      className="rb-link"
      onClick={() => {
        phStartNewCaseClicked({ source: 'closed_case_next_steps', billId: bill.id });
        navigate('/add-bill');
      }}
    >
      Start a new case
    </button>
  );

  return (
    <div className="rb">
      {/* ── The verdict ── */}
      {verdictCapable && !noSavings && !historical ? (
        <div className="rb-hero">
          <img
            src="/images/mascot/fox-email-case-closed.png"
            alt=""
            className="rb-fox"
          />
          <div className="rb-eyebrow">Case closed</div>
          {win ? (
            <div className="rb-verdict">
              You saved
            </div>
          ) : (
            <>
              <div className="rb-verdict">
                {outcomeLabel ?? 'Case closed'}
              </div>
              {explainNoFigures && <p className="rb-explain">{explainNoFigures}</p>}
            </>
          )}
          {win && (
          <div className="rb-amount num" aria-label={`You saved $${fmt2(savedTotal)}`}>
            <span className="rb-amount-cur">$</span>
            {saveDollars}
            <span className="rb-amount-cents">.{saveCents}</span>
          </div>
          )}
          {win && wasTotal > 0 && (
            <div className="rb-sub num">
              on a ${fmt2(wasTotal)} bill
              {percentSaved > 0 && (
                <>
                  {' '}· <span className="italic">a {percentSaved}% reduction.</span>
                </>
              )}
            </div>
          )}
          {win && settlements && settlements.length > 0 && (
            <div className="rb-lines">
              {settlements.map((s) => (
                <div key={s.id} className="rb-line">
                  <span className="rb-line-desc">
                    {s.description}
                    {s.rationale && <em>{s.rationale}</em>}
                  </span>
                  <span className="rb-line-amt num">&minus; ${fmt2(Number(s.saved_amount))}</span>
                </div>
              ))}
              <div className="rb-line rb-line--total">
                <span className="rb-line-desc">New balance with {providerName}</span>
                <span className="rb-line-amt num">${fmt2(nowTotal)}</span>
              </div>
            </div>
          )}
        </div>
      ) : noSavings && !historical ? (
        <div className="rb-hero rb-hero--neutral">
          <img
            src="/images/mascot/fox-email-no-savings.png"
            alt=""
            className="rb-fox"
          />
          <div className="rb-eyebrow">Case closed</div>
          <div className="rb-verdict">
            We couldn&apos;t <span className="italic">beat this one.</span>
          </div>
          {explainNoFigures && <p className="rb-explain">{explainNoFigures}</p>}
        </div>
      ) : (
        <div className="rb-plain">
          <div className="rb-kicker">Final result</div>
          <div className="rb-heading">
            {historical
              ? "We're confirming your historical result"
              : outcome
              ? CASE_OUTCOME_LABELS[outcome]
              : 'Case closed'}
          </div>
          {explainNoFigures && <p className="rb-body">{explainNoFigures}</p>}
        </div>
      )}

      {win && !historical && <ReferralAsk />}

      {/* ── What happens next ── */}
      {!historical && (
        <div className="rb-next">
          <div className="rb-next-title">What happens next</div>
          {quiet ? (
            <>
              <div className="rb-next-row">
                <span className="rb-next-num num">1</span>
                <p>Nothing further is happening on this case. The thread below stays open if you need us.</p>
              </div>
              <div className="rb-next-row">
                <span className="rb-next-num num">2</span>
                <p>Another bill? {newCaseLink}, whenever you&apos;re ready.</p>
              </div>
            </>
          ) : (
            <>
              <div className="rb-next-row">
                <span className="rb-next-num num">1</span>
                <p>
                  {paidAt ? (
                    <>
                      Paid, nice. If anything bounces back from {providerName},
                      write below and we take it from there.
                    </>
                  ) : win && nowTotal > 0 ? (
                    <>
                      Pay {providerName} the new balance of{' '}
                      <b className="num">${fmt2(nowTotal)}</b> through their portal,
                      at your pace.
                    </>
                  ) : win ? (
                    <>Nothing left to pay, the balance with {providerName} went to zero.</>
                  ) : wasTotal > 0 ? (
                    <>
                      The original balance of <b className="num">${fmt2(wasTotal)}</b>{' '}
                      stands with {providerName}, pay at your pace.
                    </>
                  ) : (
                    <>Any remaining balance stays with {providerName}, pay at your pace.</>
                  )}
                </p>
                {paidButton}
              </div>
              <div className="rb-next-row">
                <span className="rb-next-num num">2</span>
                <p>
                  Questions about the result? The thread below stays open, write any
                  time and we read it.
                </p>
              </div>
              <div className="rb-next-row">
                <span className="rb-next-num num">3</span>
                <p>Another bill? {newCaseLink}, covered by the same guarantee.</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
